import type { Desk, DeskHeartbeat, DeskPulse } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Hand, Plus } from 'lucide-react';
import { AgentAvatar, desklingMood } from '@/components/agent-avatar';
import { deskIcon, deskTone } from '@/components/desk/desk-look';
import { ActivityBars, metricInRange, RangeSwitch, rangeLabel, useActivityRange, useCardView, ViewSwitch, WorkItemCounts } from '@/components/desk/activity';
import { CardFigures, formatMetric, MetricSparkline, MetricTrend, OverlayLegend, PULSE, PulseBadge } from '@/components/desk/pulse';
import { RelativeTime } from '@/components/relative-time';
import { dotTone } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * Desk card (workspace and account level), in the landing page's look: the desk's pulse — what it is doing, its
 * activity stream (or the headline metric its Lead keeps), what waits for you and what happened lately
 * (docs/features/desk-pulse.md).
 */
export function DeskCard({ desk: d }: { desk: Desk }) {
  const tone = deskTone(d.id);
  const Icon = deskIcon(d);
  const pulse = d.pulse;
  const state = pulse?.state ?? (d.status === 'paused' ? 'paused' : 'watching');
  const ask = state === 'needs_you' ? pulse?.ask : null;
  const recent = pulse?.recent ?? [];
  // While agents run, the newest line is "now"; otherwise it is history below the waiting line.
  const live = state === 'working' || state === 'checking';
  const earlier = (live ? recent.slice(1) : recent).slice(0, 2);

  return (
    <Card
      className={cn(
        'group relative isolate flex h-full flex-col overflow-hidden p-3.5 transition-[border-color,box-shadow] duration-500 hover:shadow-md',
        tone.ring,
        ask && 'border-amber-300 shadow-lg shadow-amber-500/10 ring-2 ring-amber-400/25 dark:border-amber-700',
      )}
    >
      <div aria-hidden className={cn('pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br via-transparent to-transparent', tone.wash)} />

      <div className="flex items-center gap-2.5">
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', tone.tile)}>
          <Icon className="size-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <Link
            to="/w/$workspaceId/desks/$deskId"
            params={{ workspaceId: d.workspaceId, deskId: d.id }}
            className="block truncate text-sm font-semibold after:absolute after:inset-0 after:content-[''] group-hover:text-primary"
          >
            {d.name}
          </Link>
          <Team pulse={pulse} hasLead={!!d.leadAgent} />
        </div>
        <PulseBadge state={state} className="w-[88px]" />
      </div>

      <PulseChart desk={d} live={live} toneText={tone.text} />

      <div className="mt-3 min-h-8">
        {ask ? (
          <AskLine desk={d} pulse={pulse!} />
        ) : (
          <StatusLine desk={d} state={state} live={live} className="h-8 border-t pt-2" />
        )}
      </div>

      <Recent entries={earlier} className="mt-2" />
    </Card>
  );
}

/**
 * The desk card's pulse, wide, at the top of the desk's own page: who works, the number and the chart on the left;
 * what waits for you, what runs now and what happened lately on the right.
 */
export function DeskPulsePanel({ desk: d, className }: { desk: Desk; className?: string }) {
  const tone = deskTone(d.id);
  const pulse = d.pulse;
  const state = pulse?.state ?? (d.status === 'paused' ? 'paused' : 'watching');
  const ask = state === 'needs_you' ? pulse?.ask : null;
  const recent = pulse?.recent ?? [];
  const live = state === 'working' || state === 'checking';
  const earlier = live ? recent.slice(1) : recent;

  return (
    <Card
      className={cn(
        'group relative isolate grid gap-4 overflow-hidden p-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:gap-6',
        tone.ring,
        ask && 'border-amber-300 shadow-lg shadow-amber-500/10 ring-2 ring-amber-400/25 dark:border-amber-700',
        className,
      )}
    >
      <div aria-hidden className={cn('pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br via-transparent to-transparent', tone.wash)} />
      <div className="min-w-0">
        <Team pulse={pulse} hasLead={!!d.leadAgent} />
        <PulseChart desk={d} live={live} toneText={tone.text} chartClassName="h-12 w-auto max-w-md flex-1" />
      </div>
      <div className="flex min-w-0 flex-col gap-2 border-t pt-3 md:border-t-0 md:border-l md:pt-0 md:pl-6">
        {ask ? <AskLine desk={d} pulse={pulse!} /> : <StatusLine desk={d} state={state} live={live} className="min-h-8" />}
        <Recent entries={earlier.slice(0, 3)} />
      </div>
    </Card>
  );
}

/** One line on what the desk does right now: the newest step while agents run, otherwise what it waits for. */
function StatusLine({ desk: d, state, live, className }: { desk: Desk; state: DeskPulse['state']; live: boolean; className?: string }) {
  const pulse = d.pulse;
  const now = pulse?.recent[0];
  return (
    <div className={cn('flex items-center gap-1.5 text-xs', className)}>
      <span className={cn('size-1.5 shrink-0 rounded-full', dotTone[PULSE[state].tone], PULSE[state].live && 'animate-soft-pulse')} />
      {live && now ? (
        <span className="truncate text-foreground" title={now.message}>
          {now.message}
        </span>
      ) : (
        <span className="truncate text-muted-foreground">
          {state === 'paused' ? (
            t('Paused · no triggers, no new runs')
          ) : pulse?.nextTriggerAt ? (
            tr('Waiting for the next trigger · {time}', { time: <RelativeTime value={pulse.nextTriggerAt} /> })
          ) : d.heartbeat ? (
            heartbeat(d.heartbeat)
          ) : (
            t('Waiting for the next event')
          )}
        </span>
      )}
    </div>
  );
}

/** What happened lately, newest first, fading out. */
function Recent({ entries, className }: { entries: DeskPulse['recent']; className?: string }) {
  if (!entries.length) return null;
  return (
    <ul className={cn('space-y-1.5', className)}>
      {entries.map((e, k) => (
        <li key={`${e.at}-${k}`} className={cn('flex items-center gap-1.5 text-xs text-muted-foreground', k > 0 && k === entries.length - 1 && 'opacity-60')}>
          <span className="size-1 shrink-0 rounded-full bg-muted-foreground/50" />
          <span className="min-w-0 flex-1 truncate" title={e.message}>
            {e.message}
          </span>
          <RelativeTime value={e.at} className="shrink-0 text-[11px]" />
        </li>
      ))}
    </ul>
  );
}

/**
 * The number and the chart: by default the desk's activity stream; the Lead's headline metric on demand, with its
 * overlay metric over it when the Lead set one. Below, the
 * Work Items open now and finished within the window. The window (24h · 7d · 30d) and the view switch show on hover
 * and apply to every card.
 */
function PulseChart({ desk, live, toneText, chartClassName }: { desk: Desk; live: boolean; toneText: string; chartClassName?: string }) {
  const [range] = useActivityRange();
  const [view] = useCardView();
  const headline = desk.pulse?.headline?.latest ? desk.pulse.headline : null;
  const metric = view === 'metric' && headline ? metricInRange(headline, range) : null;
  const overlay = metric ? desk.pulse?.overlay ?? null : null;
  const buckets = desk.pulse?.activity?.[range] ?? [];
  const total = buckets.reduce((a, b) => a + b, 0);

  return (
    <div className="relative z-10 mt-3">
      <div className="flex items-end justify-between gap-3">
        {metric?.latest ? (
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl leading-none font-semibold tabular-nums">{formatMetric(metric.latest.value, metric.unit)}</span>
              <MetricTrend metric={metric} />
            </div>
            <div className="mt-1 truncate text-[11px] text-muted-foreground" title={metric.goalTitle ? t('Goal: {goal}', { goal: metric.goalTitle }) : undefined}>
              {metric.name}
            </div>
            {overlay && <OverlayLegend overlay={overlay} className="mt-0.5 max-w-full text-[11px] text-muted-foreground" />}
          </div>
        ) : (
          <div className="min-w-0">
            <div className="text-xl leading-none font-semibold tabular-nums">{total.toLocaleString(intlLocale)}</div>
            <div className="mt-1 truncate text-[11px] text-muted-foreground">
              {total === 1 ? t('event · {range}', { range: rangeLabel(range) }) : t('events · {range}', { range: rangeLabel(range) })}
            </div>
          </div>
        )}
        {metric ? (
          <MetricSparkline points={metric.points} metric={metric} overlay={overlay} className={cn('w-32', toneText, chartClassName)} />
        ) : (
          <ActivityBars buckets={buckets} range={range} live={live} className={cn('w-32', toneText, chartClassName)} />
        )}
      </div>
      <WorkItemCounts items={desk.pulse?.workItems} range={range} className="mt-2.5" />
      {!!desk.pulse?.cardMetrics?.length && <CardFigures metrics={desk.pulse.cardMetrics} className="mt-2.5" />}
      <div className="pointer-events-none absolute -top-1.5 right-0 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100">
        {headline && <ViewSwitch metricName={headline.name} />}
        <RangeSwitch />
      </div>
    </div>
  );
}

/** "Waiting · the Lead checks every …" */
function heartbeat(hb: DeskHeartbeat) {
  if ('at' in hb) {
    if (hb.every === 'daily') return t('Waiting · the Lead checks daily at {time}', { time: hb.at });
    if (hb.every === 'weekly') return t('Waiting · the Lead checks Mondays at {time}', { time: hb.at });
    return t('Waiting · the Lead checks on the 1st at {time}', { time: hb.at });
  }
  if (hb.every === 'hours') return hb.n === 1 ? t('Waiting · the Lead checks every hour') : t('Waiting · the Lead checks every {n} h', { n: hb.n });
  return t('Waiting · the Lead checks every {n} min', { n: hb.n });
}

/** The team's avatars (the Lead first, then whoever runs right now) and how many of them work at the moment. */
function Team({ pulse, hasLead }: { pulse: DeskPulse | undefined; hasLead: boolean }) {
  if (!hasLead) return <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">{t('No Lead Agent')}</div>;
  const team = [...(pulse?.team ?? [])].sort((a, b) => Number(b.role === 'lead') - Number(a.role === 'lead') || Number(b.working) - Number(a.working));
  const shown = team.slice(0, 5);
  const working = team.filter((a) => a.working).length;
  return (
    <div className="mt-1 flex items-center">
      <div className="flex -space-x-1">
        {shown.map((a) => (
          <span
            key={a.agentId}
            title={[a.role === 'lead' ? t('{name} (Lead)', { name: a.name }) : a.name, a.working && t('working now')].filter(Boolean).join(' · ')}
            className={cn('relative rounded-full ring-2 ring-card', working > 0 && !a.working && 'opacity-50')}
          >
            <AgentAvatar seed={a.agentId} size={18} mood={desklingMood(pulse, a.agentId)} />
            {a.working && <span className={cn('absolute -right-px -bottom-px size-[7px] rounded-full ring-[1.5px] ring-card animate-soft-pulse', dotTone.green)} />}
          </span>
        ))}
      </div>
      {team.length > shown.length && <span className="ml-1.5 text-[11px] text-muted-foreground">+{team.length - shown.length}</span>}
      <span className="ml-1.5 truncate text-[11px] text-muted-foreground tabular-nums">
        {working > 0
          ? tr('{n} of {total} working', { n: <span className="font-medium text-foreground">{working}</span>, total: team.length })
          : team.length === 1
            ? t('1 agent')
            : t('{n} agents', { n: team.length })}
      </span>
    </div>
  );
}

/** The oldest thing that waits for a person: approve it right here, or open it. */
function AskLine({ desk, pulse }: { desk: Desk; pulse: DeskPulse }) {
  const ask = pulse.ask!;
  // Something only a person can do is marked done, not approved; its steps are on the desk page.
  const person = ask.kind === 'person';
  const approve = useApiMutation(() => api.approvals.approve(ask.approvalId!), {
    invalidate: [qk.desks(desk.workspaceId), qk.desk(desk.id), qk.approvalsAll(desk.workspaceId), qk.workItem(ask.workItemId)],
    success: person ? t('Thanks — the Lead goes on.') : t('Approved — the desk goes on.'),
  });
  const more = pulse.needsYou - 1;
  return (
    <div className="flex h-8 items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 pr-1 pl-2.5 animate-in fade-in zoom-in-95 duration-300 dark:border-amber-800/60 dark:bg-amber-950/30">
      <Hand className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="min-w-0 flex-1 truncate text-xs font-medium" title={ask.text}>
        {ask.text}
      </span>
      {more > 0 && <span className="shrink-0 text-[11px] text-amber-700 tabular-nums dark:text-amber-400">+{more}</span>}
      {(ask.kind === 'approval' || person) && ask.approvalId ? (
        <button
          type="button"
          disabled={approve.isPending}
          onClick={() => approve.mutate()}
          className="relative z-10 inline-flex h-6 shrink-0 items-center rounded-md bg-primary px-2 text-[11px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <span className="absolute inset-0 animate-ping rounded-md bg-primary/40 [animation-duration:1.8s]" />
          <span className="relative">{person ? (approve.isPending ? t('Saving…') : t('Done')) : approve.isPending ? t('Approving…') : t('Approve')}</span>
        </button>
      ) : (
        <Link
          to="/w/$workspaceId/work-items/$workItemId"
          params={{ workspaceId: desk.workspaceId, workItemId: ask.workItemId }}
          className="relative z-10 inline-flex h-6 shrink-0 items-center gap-1 rounded-md bg-primary px-2 text-[11px] font-medium text-primary-foreground hover:opacity-90"
        >
          {ask.kind === 'question' ? t('Answer') : t('Decide')} <ArrowRight className="size-3" />
        </Link>
      )}
    </div>
  );
}

/** Dashed "+" tile after a workspace's desks: opens the workspace's start page to set up a new desk. */
export function NewDeskCard({ workspaceId }: { workspaceId: string }) {
  return (
    <Link to="/w/$workspaceId/start" params={{ workspaceId }} className="group" aria-label={t('Create a new desk')}>
      <Card className="flex h-full min-h-32 flex-col items-center justify-center gap-1.5 border-dashed p-4 text-muted-foreground shadow-none transition-colors group-hover:border-primary/40 group-hover:text-primary">
        <Plus className="size-6" />
        <span className="text-sm font-medium">{t('New desk')}</span>
        <span className="text-[11px]">{t('One sentence. The Lead staffs the rest.')}</span>
      </Card>
    </Link>
  );
}
